import React, { useEffect, useState } from "react";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Pagination from "@mui/material/Pagination";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import EditDialog from "components/Dialogs/EditDialog";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import {
  getInfluencers,
  deleteInfluencer,
  updateInfluencer,
  exportInfluencersToExcel,
} from "data/controllers/contestController";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";

const influencerColumns = [
  { Header: "Name", accessor: "name", align: "left" },
  { Header: "Referral Code", accessor: "referralCode", align: "center" },
  { Header: "Downloads", accessor: "downloadCounts", align: "center" },
  { Header: "Points", accessor: "influencerPoints", align: "center" },
  { Header: "Created At", accessor: "createdAt", align: "center" },
  { Header: "Actions", accessor: "action", align: "center" },
];

function Contest() {
  const [influencerRows, setInfluencerRows] = useState([]);
  const [influencersData, setInfluencersData] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");

  const [influencerToDelete, setInfluencerToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewLinks, setViewLinks] = useState([]);

  // Edit Influencer dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    referralCode: "",
    influencerPoints: 0,
    downloadCounts: 0,
    videoUrl: [""],
  });
  const [selectedInfluencerId, setSelectedInfluencerId] = useState(null);

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportSnackbar, setExportSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });



  // Submit edit handler
  const handleSubmitEdit = async () => {
    try {
      await updateInfluencer(selectedInfluencerId, editForm);

      // Update the influencersData state
      setInfluencersData((prev) =>
        prev.map((user) => (user._id === selectedInfluencerId ? { ...user, ...editForm } : user))
      );

      // Update the influencerRows state immediately to reflect changes
      setInfluencerRows((prev) =>
        prev.map((row) => {
          if (row._originalData?._id === selectedInfluencerId) {
            const updatedUser = { ...row._originalData, ...editForm };
            return {
              ...row,
              referralCode: (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  {updatedUser.referralCode}
                </MDTypography>
              ),
              downloadCounts: (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  {updatedUser.downloadCounts}
                </MDTypography>
              ),
              influencerPoints: (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  {updatedUser.influencerPoints}
                </MDTypography>
              ),
              _originalData: updatedUser,
            };
          }
          return row;
        })
      );

      setEditDialogOpen(false);
    } catch (error) {
      setErrorDialogMessage(error.message || "Failed to update influencer");
      setErrorDialogOpen(true);
    }
  };

  useEffect(() => {
    async function fetchInfluencers() {
      try {
        const { influencers, pagination } = await getInfluencers(page, limit);
        setInfluencersData(influencers);
        setTotalPages(Math.ceil((pagination?.total || 0) / limit));

        // Process data immediately after fetching to avoid double processing
        const formattedInfluencers = influencers.map((user) => ({
          name: (
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDAvatar src={user.profilePic} name={user.name || "User"} size="sm" />
              <MDBox display="flex" flexDirection="column">
                <MDTypography variant="button" fontWeight="medium">
                  {user.name}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {user.videoUrl?.length || 0} {(user.videoUrl?.length || 0) === 1 ? "link" : "links"}
                </MDTypography>
              </MDBox>
            </MDBox>
          ),
          referralCode: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {user.referralCode}
            </MDTypography>
          ),
          downloadCounts: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {user.downloadCounts}
            </MDTypography>
          ),
          influencerPoints: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {user.influencerPoints}
            </MDTypography>
          ),
          createdAt: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {user.createdAt
                ? new Date(user.createdAt).toLocaleString(undefined, {
                    day: "2-digit",
                    month: "2-digit",
                    year: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                : ""}
            </MDTypography>
          ),
          action: (
            <MDBox display="flex" alignItems="center" gap={1}>
              <IconButton size="small" color="info" onClick={() => handleViewClick(user.videoUrl)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="info" onClick={() => handleEditClick(user)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => handleDeleteClick(user._id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </MDBox>
          ),
          // Store original data for edit operations
          _originalData: user,
        }));
        setInfluencerRows(formattedInfluencers);
      } catch (error) {
        setErrorDialogMessage(error.message || "Failed to fetch influencers");
        setErrorDialogOpen(true);
      }
    }
    fetchInfluencers();
  }, [page, limit]);

  // Edit dialog handler
  const handleEditClick = (user) => {
    setEditForm({
      referralCode: user.referralCode || "",
      influencerPoints: user.influencerPoints || 0,
      downloadCounts: user.downloadCounts || 0,
      videoUrl: user.videoUrl && user.videoUrl.length > 0 ? [...user.videoUrl] : [""],
    });
    setSelectedInfluencerId(user._id);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (jobId) => {
    setInfluencerToDelete(jobId);
    setConfirmDialogOpen(true);
  };

  const confirmDeleteJob = async () => {
    if (!influencerToDelete) return;
    setConfirmDialogOpen(false);
    setIsDeleting(true);
    try {
      await deleteInfluencer(influencerToDelete);

      // Update both states to remove the deleted influencer
      setInfluencersData((prev) => prev.filter((influencer) => influencer._id !== influencerToDelete));
      setInfluencerRows((prev) => prev.filter((row) => row._originalData?._id !== influencerToDelete));
    } catch (error) {
      setErrorDialogMessage(error.message || "Failed to delete influencer");
      setErrorDialogOpen(true);
    } finally {
      setIsDeleting(false);
      setInfluencerToDelete(null);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleViewClick = (links) => {
    setViewLinks(links);
    setViewDialogOpen(true);
  };

  // Handle Excel export
  const handleExportToExcel = async () => {
    setIsExporting(true);
    try {
      const result = await exportInfluencersToExcel();
      setExportSnackbar({
        open: true,
        message: `Successfully exported ${result.totalRecords} influencers to ${result.filename}`,
        severity: "success"
      });
    } catch (error) {
      console.error("Export failed:", error);
      setExportSnackbar({
        open: true,
        message: error.message || "Failed to export data to Excel",
        severity: "error"
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setExportSnackbar({ ...exportSnackbar, open: false });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 2, sm: 0 },
                  textAlign: { xs: "center", sm: "left" }
                }}
              >
                <MDTypography variant="h6" color="white" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                  Influencer hunt Contest
                </MDTypography>
                <Button
                  variant="contained"
                  startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
                  onClick={handleExportToExcel}
                  disabled={isExporting}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                    color: "#ffffff",
                    fontWeight: "medium",
                    fontSize: { xs: "0.65rem", sm: "0.875rem" },
                    padding: { xs: "4px 8px", sm: "8px 16px" },
                    minWidth: { xs: "auto", sm: "140px" },
                    height: { xs: "28px", sm: "auto" },
                    "& .MuiButton-startIcon": {
                      marginRight: { xs: "2px", sm: "8px" },
                      "& svg": {
                        fontSize: { xs: "12px", sm: "20px" }
                      }
                    },
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.4)",
                      color: "#ffffff",
                    },
                    "&:disabled": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      color: "#ffffff",
                      opacity: 0.7,
                    }
                  }}
                >
                  <MDBox
                    component="span"
                    sx={{
                      display: { xs: "none", sm: "inline" },
                      color: "#ffffff"
                    }}
                  >
                    {isExporting ? "Exporting..." : "Export to Excel"}
                  </MDBox>
                  <MDBox
                    component="span"
                    sx={{
                      display: { xs: "inline", sm: "none" },
                      color: "#ffffff",
                      fontSize: "0.65rem",
                      fontWeight: "medium"
                    }}
                  >
                    {isExporting ? "EXPORTING..." : "EXPORT TO EXCEL"}
                  </MDBox>
                </Button>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns: influencerColumns, rows: influencerRows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={2}
                mt={2}
                px={3}
              >
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton={window.innerWidth > 768}
                  showLastButton={window.innerWidth > 768}
                  siblingCount={window.innerWidth > 768 ? 1 : 0}
                  boundaryCount={window.innerWidth > 768 ? 1 : 1}
                  sx={{
                    ml: { xs: 0, sm: 2 },
                    "& .MuiPaginationItem-root": {
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      minWidth: { xs: "28px", sm: "32px" },
                      height: { xs: "28px", sm: "32px" },
                    }
                  }}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <ConfirmDialog
        open={confirmDialogOpen}
        title="Confirm Deletion"
        description="Are you sure you want to delete this influencer? This action cannot be undone."
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmDeleteJob}
        confirmButtonText="Delete"
        confirmButtonColor="error"
      />
      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title">{"Error"}</DialogTitle>
        <DialogContent dividers>
          <MDTypography>{errorDialogMessage}</MDTypography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setErrorDialogOpen(false)}
            color="primary"
            variant="contained"
            autoFocus
            sx={{
              height: "40px",
              backgroundColor: "#1976d2",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)}>
        <DialogTitle>Influencer Links</DialogTitle>
        <DialogContent dividers>
          <MDBox component="ul" pl={2}>
            {viewLinks.map((link, index) => (
              <li key={index}>
                <a href={link} target="_blank" rel="noopener noreferrer">
                  {link}
                </a>
              </li>
            ))}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)} color="primary" autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <EditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title="Edit Influencer"
        fields={[
          { label: "Referral Code", key: "referralCode", type: "text" },
          { label: "Influencer Points", key: "influencerPoints", type: "number" },
          { label: "Download Counts", key: "downloadCounts", type: "number" },
        ]}
        videoUrlKey="videoUrl"
        formData={editForm}
        setFormData={setEditForm}
        onSubmit={handleSubmitEdit}
      />

      {/* Export Success/Error Snackbar */}
      <Snackbar
        open={exportSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={exportSnackbar.severity}
          sx={{ width: '100%' }}
        >
          {exportSnackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Contest;
